import { useCallback, useEffect, useState } from 'react'

import { quantApi } from '@/app/lib/quant/quantApi'
import { isQuantAbort } from '@/app/lib/quant/quantErrors'
import { savedConfigErrorMessage } from '@/app/lib/quant/savedConfigs'
import type { ParamValue, SavedConfig } from '@/app/lib/quant/types'

export type SavedConfigsState =
  | { status: 'loading' }
  | { status: 'loaded'; configs: SavedConfig[] }
  | { status: 'error'; message: string }

export interface SavedConfigsApi {
  state: SavedConfigsState
  /** Newest-updated first, as the server returns it; empty until loaded. */
  configs: SavedConfig[]
  /** Non-optimistic: resolves with null on success (the list is refetched), else the error sentence. */
  save: (name: string, params: Record<string, ParamValue | null>) => Promise<string | null>
  remove: (name: string) => Promise<string | null>
  refresh: () => Promise<void>
}

/** The saved configs of one strategy (`/quant/strategies/{slug}/configs`): plain state, refetched after every mutation.
 * State is keyed by slug (no ref writes during render, no synchronous setState in the effect): a result for
 * another slug reads as `loading` until this slug's own answer arrives. */
export function useQuantSavedConfigs(slug: string): SavedConfigsApi {
  type Keyed = { slug: string; value: Exclude<SavedConfigsState, { status: 'loading' }> }
  const [keyed, setKeyed] = useState<Keyed | null>(null)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    quantApi
      .listSavedConfigs(slug, controller.signal)
      .then((body) => {
        if (!cancelled) setKeyed({ slug, value: { status: 'loaded', configs: body.configs } })
      })
      .catch((error: unknown) => {
        if (cancelled || isQuantAbort(error)) return
        setKeyed({ slug, value: { status: 'error', message: savedConfigErrorMessage(error) } })
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [slug, version])

  const refresh = useCallback(async () => {
    setVersion((v) => v + 1)
  }, [])

  const save = useCallback(
    async (name: string, params: Record<string, ParamValue | null>) => {
      try {
        await quantApi.saveConfig(slug, name, params)
      } catch (error) {
        return savedConfigErrorMessage(error)
      }
      setVersion((v) => v + 1)
      return null
    },
    [slug],
  )

  const remove = useCallback(
    async (name: string) => {
      try {
        await quantApi.deleteConfig(slug, name)
      } catch (error) {
        return savedConfigErrorMessage(error)
      }
      setVersion((v) => v + 1)
      return null
    },
    [slug],
  )

  const state: SavedConfigsState = keyed !== null && keyed.slug === slug ? keyed.value : { status: 'loading' }
  return {
    state,
    configs: state.status === 'loaded' ? state.configs : [],
    save,
    remove,
    refresh,
  }
}
